import { IObservableArray } from 'mobx';
import React from 'react';

import { GridColumnConfig } from 'controls/grid/gridColumnConfig';
import { GridDataEntry } from 'controls/grid/gridDataEntry';
import { GridStore } from './gridStore';
import { RulesConfiguration } from 'controls/queryBuilder/ruleDefinition';
import { GridState } from './gridState';
import { GridViewState } from './gridViewState';
import { GridStateProvider } from 'controls/grid/stateProviders';
import { GridDataItem } from 'controls/grid/gridDataItem';
import { GridSelectionState } from 'controls/grid/gridSelection';
import { CSSProperties } from 'react';
import { ApiRequest } from 'framework/api';

export enum GridSelectionType {
	None,
	One,
	Many,
}

export interface DataProvider<DataEntry extends GridDataEntry> {
	data: IObservableArray<GridDataItem<DataEntry>>;
	initialized: boolean;
	showLoader: boolean;

	//total number of data rows
	visibleRowsCount: number;
	//total number of rows in the grid including group headers (since they are represented as a grid row)
	scrollableRowsCount: number;
	//unfiltered rows count
	totalRowsCount: number;
	failToLoad: boolean;

	attach: (store: GridStore<DataEntry>) => Promise<void>;
	//reload: () => void
	silentReload: () => void;
	informVisibleRangeChanged: (startIndex: number, stopIndex: number) => void;

	get: (index: number) => GridDataItem<DataEntry>;

	destroy: () => void;

	groupBySupported?: boolean;
}

export type GridDefaultFilterEntry = {
	field: string;
	operator: string;
	value: string[];
};

export type GridConfiguration<DataItem extends GridDataEntry> = {
	name?: string;

	dataProvider: DataProvider<DataItem>;
	filtersConfiguration?: RulesConfiguration | ApiRequest<RulesConfiguration>;
	idKey?: keyof DataItem;
	columns: GridColumnConfig<DataItem>[] | (() => GridColumnConfig<DataItem>[]);
	autoWidth?: boolean;

	selection?: GridSelectionType;

	plugins?: Array<GridPlugin<DataItem>>;

	persistState?: boolean;

	state?: GridState<DataItem>;

	fit?: boolean;

	stateProviders?: GridStateProvider<DataItem>[];

	onLoadErrorMessage?: string;
	expandOnClick?: boolean;

	onFiltersCleared?: (store: GridStore<DataItem>) => void;

	groupBy?: boolean;

	//will be shown under the grid header on initial load
	informationMessage?: string;

	doNotInitOnCreate?: boolean;

	features?: {
		groupByPath?: boolean;
	};

	columnsSelector?: {
		selection?: GridSelectionType;
	};

	defaults?: {
		//look at GridViewState.payload field comment to get the difference between payload and data
		payload?: Record<string, unknown>;
		data?: Record<string, object>;
		sorting?: { field: string; direction: 'asc' | 'desc' }[];
		columns?: string[];
		filters?: GridDefaultFilterEntry[];
		selectionState?: Partial<GridSelectionState>;
		selectedIds?: string[];
		showInlineFilters?: boolean;
	};

	getRowOptions?: (
		item: GridDataItem<DataItem>,
		store: GridStore<DataItem>,
	) => {
		selection?: {
			disabled?: boolean;
			disabledReason?: string;
		};
		grayedOut?: boolean;
		title?: string;
	};

	//renders arbitrary content in a panel below a row, toggled by an expander column added automatically on the left
	rowExpansion?: {
		renderer: (item: DataItem, gridItem: GridDataItem<DataItem>) => React.ReactNode;
	};

	customization?: {
		styling?: GridCustomStyling;
		theme?: 'default' | 'light';
		getRowCustomization?: (item: GridDataItem<DataItem>) => {
			className?: string;
			disabled?: boolean;
			invalid?: boolean;
			style?: CSSProperties;
			tooltip?: string;
		} | null;
		//number: fixed pixel height. 'by-content': grows to fit all rows (optionally capped, then scrolls).
		//'fit' (default when omitted): fills the available space of the parent container.
		height?: number | 'by-content' | 'fit' | { type: 'by-content'; maxHeight: number };
		emptyMessage?: string;
		emptyMessageOnClick?: () => void;
	};
};

export type GridCustomStyling = {
	transparent?: boolean;
	backgroundColor?: string;
	fontSize?: string;
	color?: string;
	fontFamily?: string;
	fontWeight?: string;
	fontStyle?: string;
	textDecoration?: string;
	defaultRowHeight?: number;
};

export interface GridPlugin<DataItem extends GridDataEntry> {
	id?: string;
	attach(store: GridStore<DataItem>): Promise<void>;
	afterStoreInitialized?: () => void;
	applyToView?: (view: GridViewState<DataItem>) => void;
	columnsFilter?: (columns: GridColumnConfig<DataItem>[]) => GridColumnConfig<DataItem>[];
	destroy?: () => void;
}
